/**
 * YUMIX — FavoritesModal (Iter. 396 · Lot #12)
 *
 * Drawer listing the user's saved favorite ingredients. Each card supports:
 *   - One-click "Add to today" (the green +).
 *   - Inline ✏️ rename → custom_name field (PATCH /scan/favorites/{barcode}/rename).
 *   - Delete (trash icon).
 *
 * The custom_name takes precedence over product_name in the display, so a
 * user who registered an auto-named meal as `pain + mayo + dinde` can
 * relabel it to "Mon déjeuner classique" and find it instantly in the list.
 */
import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Heart, Plus, Trash2, Loader2, Search, Pencil, Check, X as XIcon } from 'lucide-react';
import { useLanguage } from '../../context/AppContext';

const FavoritesModal = ({
  open,
  onOpenChange,
  favorites,
  loadingFavorites,
  addingFavorite,
  onAddFavorite,
  onDeleteFavorite,
  onRenameFavorite, // (barcode, customName) => Promise<void>
}) => {
  const { language } = useLanguage();
  const [query, setQuery] = useState('');
  // Inline edit state — one favorite at a time.
  const [editingBarcode, setEditingBarcode] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return favorites;
    return favorites.filter((f) => {
      const display = (f.custom_name || f.product_name || '').toLowerCase();
      return display.includes(q) || (f.brand || '').toLowerCase().includes(q);
    });
  }, [favorites, query]);

  const startEdit = (fav) => {
    setEditingBarcode(fav.barcode);
    setEditValue(fav.custom_name || fav.product_name || '');
  };

  const cancelEdit = () => {
    setEditingBarcode(null);
    setEditValue('');
  };

  const commitEdit = async () => {
    if (!editingBarcode || !onRenameFavorite) { cancelEdit(); return; }
    setSavingRename(true);
    try {
      await onRenameFavorite(editingBarcode, editValue.trim());
      cancelEdit();
    } finally {
      setSavingRename(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md flex flex-col max-h-[85vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-3">
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-500" />
            {language === 'fr' ? 'Mes Favoris' : language === 'es' ? 'Mis Favoritos' : 'My Favorites'}
            {favorites.length > 0 && (
              <span className="text-xs font-normal text-muted-foreground ml-1">
                ({favorites.length})
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {language === 'fr'
              ? 'Ajoute rapidement tes repas habituels. ✏️ pour renommer.'
              : language === 'es'
              ? 'Añade tus comidas habituales. ✏️ para renombrar.'
              : 'Quickly add your regular meals. ✏️ to rename.'}
          </DialogDescription>
        </DialogHeader>

        {/* Search bar — shown when the list gets long */}
        {!loadingFavorites && favorites.length > 5 && (
          <div className="px-6 pb-3">
            <div className="relative">
              <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={language === 'fr' ? 'Rechercher un favori…' : language === 'es' ? 'Buscar un favorito…' : 'Search a favorite…'}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-pink-500/40 placeholder:text-muted-foreground"
                data-testid="favorites-modal-search"
              />
            </div>
          </div>
        )}

        {/* Scrollable list */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-3" data-testid="favorites-modal-list">
          {loadingFavorites ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-10">
              <Heart className="w-12 h-12 text-pink-400/40 mx-auto mb-3" />
              <p className="text-foreground font-medium mb-1">
                {language === 'fr'
                  ? 'Ajoute tes repas ou exercices préférés ici !'
                  : language === 'es'
                  ? '¡Añade tus comidas o ejercicios favoritos aquí!'
                  : 'Add your favorite meals or exercises here!'}
              </p>
              <p className="text-xs text-muted-foreground">
                {language === 'fr'
                  ? 'Depuis un scan ou une analyse, clique sur "Ajouter aux favoris".'
                  : language === 'es'
                  ? 'Desde un escaneo o análisis, haz clic en "Añadir a favoritos".'
                  : 'From a scan or analysis, click "Add to favorites".'}
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              {language === 'fr' ? 'Aucun résultat' : language === 'es' ? 'Sin resultados' : 'No results'}
            </p>
          ) : (
            filtered.map((favorite) => {
              const isHot = !!favorite.is_hot;
              const isEditing = editingBarcode === favorite.barcode;
              const displayName = favorite.custom_name || favorite.product_name;
              const isRenamed = Boolean(favorite.custom_name);
              return (
              <div
                key={favorite.fav_id || favorite.barcode}
                className={`p-4 rounded-lg flex items-center justify-between gap-3 transition-colors ${
                  isHot
                    ? 'bg-gradient-to-r from-orange-500/15 to-pink-500/10 border border-orange-500/30'
                    : 'bg-secondary'
                }`}
                data-testid={`favorite-card-${favorite.barcode}`}
              >
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    /* Inline edit mode — text input with check / cancel buttons */
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        autoFocus
                        value={editValue}
                        maxLength={80}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitEdit();
                          else if (e.key === 'Escape') cancelEdit();
                        }}
                        placeholder={language === 'fr' ? 'Ex. Mon déjeuner classique' : language === 'es' ? 'Ej. Mi desayuno clásico' : 'e.g. My classic breakfast'}
                        className="flex-1 min-w-0 px-2 py-1.5 text-sm rounded bg-background border border-pink-500/50 focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                        data-testid={`favorite-rename-input-${favorite.barcode}`}
                      />
                      <Button
                        size="sm"
                        onClick={commitEdit}
                        disabled={savingRename}
                        className="h-8 px-2 bg-emerald-500 hover:bg-emerald-600 text-white"
                        data-testid={`favorite-rename-save-${favorite.barcode}`}
                      >
                        {savingRename ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelEdit}
                        className="h-8 px-2"
                        data-testid={`favorite-rename-cancel-${favorite.barcode}`}
                      >
                        <XIcon className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    /* Display mode */
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        {isHot && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/25 text-orange-300 font-bold flex items-center gap-0.5"
                            title={language === 'fr' ? `Loggé ${favorite.log_count_7d}× cette semaine` : `Logged ${favorite.log_count_7d}× this week`}
                            data-testid={`favorites-modal-hot-${favorite.barcode}`}
                          >
                            🔥 <span className="tabular-nums">{favorite.log_count_7d}</span>
                          </span>
                        )}
                        <span
                          className="font-heading text-foreground truncate"
                          data-testid={`favorite-name-${favorite.barcode}`}
                          title={isRenamed ? `${language === 'fr' ? 'Original' : 'Original'}: ${favorite.product_name}` : displayName}
                        >
                          {displayName}
                        </span>
                        {favorite.brand && (
                          <span className="text-xs bg-pink-500/20 text-pink-500 px-2 py-0.5 rounded-full">
                            {favorite.brand}
                          </span>
                        )}
                        {isRenamed && (
                          <span
                            className="text-[9px] uppercase tracking-wider text-pink-400/70"
                            title={`${language === 'fr' ? 'Nom original' : 'Original name'}: ${favorite.product_name}`}
                          >
                            ✓ {language === 'fr' ? 'perso' : 'custom'}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                        <span className="text-primary font-medium">{favorite.nutrition_per_100g?.protein ?? 0}g prot</span>
                        <span>{favorite.nutrition_per_100g?.calories ?? 0} cal</span>
                        <span>{favorite.nutrition_per_100g?.carbs ?? 0}g gluc</span>
                      </div>
                    </>
                  )}
                </div>
                {!isEditing && (
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      onClick={() => onAddFavorite(favorite)}
                      disabled={addingFavorite}
                      className="bg-primary text-primary-foreground h-9 px-3"
                      data-testid={`add-favorite-${favorite.barcode}`}
                    >
                      {addingFavorite ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(favorite)}
                      className="text-muted-foreground hover:text-pink-400 h-9 px-2"
                      data-testid={`favorite-edit-${favorite.barcode}`}
                      title={language === 'fr' ? 'Renommer' : language === 'es' ? 'Renombrar' : 'Rename'}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDeleteFavorite(favorite.barcode)}
                      className="text-muted-foreground hover:text-destructive h-9 px-2"
                      data-testid={`delete-favorite-${favorite.barcode}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FavoritesModal;
